/**
 * FAQ Filter Interface
 * Represents the filter parameters for FAQ queries
 */
export interface FaqFilter {
    skip: number;
    take: number;
    startDate?: string;
    endDate?: string;
    paging: boolean;
    searchString?: string;
    categoryId?: string;
    categoryIds?: string[];
}

/**
 * FAQ Filter Model
 * Encapsulates FAQ filter logic following Single Responsibility Principle
 * Responsibility: Transform and validate FAQ filter parameters
 */
export class FaqFilterModel {
    skip: number;
    take: number;
    startDate: string;
    endDate: string;
    paging: boolean;
    searchString: string;
    categoryIds: string[];

    constructor(model: Partial<FaqFilter>) {
        this.skip = model.skip ?? 0;
        this.take = model.take ?? 10;
        this.startDate = model.startDate ?? '';
        this.endDate = model.endDate ?? '';
        this.paging = model.paging ?? true;
        this.searchString = model.searchString ?? '';
        this.categoryIds = model.categoryIds ?? [];
    }
}

/**
 * FAQ Category Interface
 * Represents a FAQ category hierarchy
 */
export interface FaqCategory {
    id: string;
    name: string;
    childCategories?: FaqCategory[];
}

/**
 * FAQ Category Model
 * Encapsulates FAQ category data and behavior
 * Responsibility: Manage FAQ category data structure
 */
export class FaqCategoryModel {
    id: string;
    name: string;
    childCategories: FaqCategoryModel[];

    constructor(model: FaqCategory) {
        this.id = model.id;
        this.name = model.name;
        this.childCategories = model.childCategories
            ? model.childCategories.map(c => new FaqCategoryModel(c))
            : [];
    }
}

/**
 * FAQ Item Interface
 * Represents a single FAQ entry
 */
export interface Faq {
    id: string;
    question: string;
    answer: string;
    categoryId: string;
    categoryName: string;
    order: number;
    isActive: boolean;
    createdDate?: string;
    modifiedDate?: string;
}

/**
 * FAQ Item Model
 * Encapsulates FAQ item data and UI state
 * Responsibility: Manage FAQ item data and expansion state
 */
export class FaqModel {
    id: string;
    question: string;
    answer: string;
    categoryName: string;
    // UI-specific state
    isExpanded: boolean;

    constructor(model: Faq) {
        this.id = model.id;
        this.question = model.question;
        this.answer = model.answer;
        this.categoryName = model.categoryName;
        this.isExpanded = false; // Default to collapsed
    }
}

/**
 * FAQ Hierarchy Request Interface
 * Represents request body for hierarchy endpoint
 */
export interface FaqCategoryHierarchyRequest {
    skip: string;
    take: string;
    startDate: string;
    endDate: string;
    paging: string;
    searchString: string;
    includeEmptyCategories: boolean;
    isPublished?: boolean|null;
    isDeleted?: boolean|null;
}

/**
 * FAQ Hierarchy Response Interface
 * Represents response from hierarchy endpoint
 */
export interface FaqHierarchyResponse {
    skip: string;
    take: string;
    totalCount: string;
    pageIndex: string;
    perPage: string;
    totalPages: string;
    data: FaqCategory[];
}
